<template>
    <tr :class="{ 'unsaved-changes': form.hasUpdated }" v-show="hasUnsavedChanges" @dblclick="toggleEdit">
        <td>
            <input type="checkbox" v-model="isSelected" @input="toggleSelected" @click.stop />
        </td>
        <td>
            <Link :href="productUrl" preserve-scroll class="underline" @click.stop>
                {{ product.id }}
            </Link>
        </td>
        <td>
            <span v-if="product.is_master_product">{{ product.id }}</span>
            <span v-else>{{ product.related_master_product_id }}</span>
        </td>
        <td>
            {{ product.title }}
            <span v-if="product.description">- {{ product.description }}</span>
            <span v-if="product.size">- {{ product.size }}</span>
        </td>
        <td class="text-center">
            <toggle v-model="form.enabled" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.local" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.hot_buy" @click.stop/>
        </td>
        <td class="text-center whitespace-nowrap">
            <div class="inline-flex items-center justify-center gap-2">
                <toggle v-model="form.special_order" @click.stop/>
                <special-order-lead-time
                    v-model="form.special_order_lead_hours"
                    :disabled="!form.special_order"
                />
            </div>
            <jet-input-error :message="getError('special_order_lead_hours')" />
        </td>
        <td class="text-center">
            <toggle v-model="form.allow_oversell" @click.stop/>
        </td>
        <td class="text-center">
            {{ form.stock }}
        </td>
        <td v-if="hasMarketCostProducts" class="text-center price-input">
            <div v-if="isMarketCostCategory">
                <jet-input
                    v-model.number="market_cost"
                    type="number"
                    class="text-right"
                    step="any"
                    @click.stop
                />

                <jet-input-error :message="getError('market_cost')" />
            </div>
        </td>
        <td class="text-center">
            {{ form.cost }}
        </td>
        <td class="text-center price-input">
            <jet-input
                v-model.number="price"
                type="number"
                class="text-right"
                step="any"
                @click.stop
            />

            <jet-input-error :message="getError('price')" />
        </td>

        <td v-for="(pricing, index) in form.pricing_levels" :key="pricing.pricing_level_id" class="text-right price-select">
            <price-select
                v-if="hasUnsavedChanges && (editing || isEditingPricingLevel(pricing.pricing_level_id))"
                class="price-select-sm"
                :pricing="pricing"
                @price-level-update="handlePriceLevelUpdate"
                @click.stop
                @dblclick.stop
            />

            <p v-else class="text-center">
                {{ actualPrice(pricing) }}
            </p>

            <div v-if="pricing.pricing_level_title.includes('Retail')" class="price-select-sm">
                <div class="price-select-sm mt-1" v-if="hasUnsavedChanges && (editing || isEditingPricingLevel(pricing.pricing_level_id))">
                    <input
                        v-model="price_2plus"
                        id="price-2plus"
                        type="number"
                        step="any"
                        class="rounded-l rounded-r-none"
                        :disabled="type === '' || type === null || type === 'disabled'"
                        @click.stop
                    />

                    <select class="rounded-r rounded-l-none" v-model="type" @change="modifyPriceTwoPlus">
                        <option value="fixed">$</option>
                        <option value="disabled">X</option>
                    </select>
                </div>
                <p v-else-if="price_2plus > 0" class="text-center">{{ priceToPlusView(price_2plus) }} (2+)</p>
            </div>

            <jet-input-error :message="getPricingLevelError(index, 'modifier_amount')" />
        </td>

        <td class="text-right">
            <jet-secondary-button type="button" @click.stop="toggleEdit" style="padding-left: 0.25rem; padding-right: 0.25rem; min-width: 24px;">
                <img v-if="editing" src="/images/icons/x-white.svg" width="18" height="18" />
                <img v-else src="/images/icons/edit.svg" width="18" height="18" />
            </jet-secondary-button>
        </td>
    </tr>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import PriceSelect from '@/Pages/Pricing/Partials/PriceSelect';
import { Link } from '@inertiajs/inertia-vue3';
import Toggle from '@vueform/toggle';
import SpecialOrderLeadTime from '@/Shared/SpecialOrderLeadTime.vue';
import { getCurrentInstance } from "vue";

import '@vueform/toggle/themes/default.css';

export default {
    components: {
        Link,
        PriceSelect,
        JetInput,
        JetInputError,
        JetSecondaryButton,
        Toggle,
        SpecialOrderLeadTime,
    },

    props: ['form', 'product', 'filters', 'categories', 'selected', 'editing', 'editingPriceLevels', 'errors', 'productIndex', 'updatedProducts', 'hasMarketCostProducts'],

    data() {
        return {
            isSelected: this.selected,
            type: 'disabled',
        }
    },

    computed: {
        price_2plus: {
            get () {
                return this.form.price_2plus;
            },
            set (newValue) {
                this.$emit('productUpdate', {
                    cost: this.form.cost,
                    price_2plus: newValue ? Number(newValue) : null,
                    price: this.price ? Number(this.price) : null,
                    market_cost: this.form.market_cost,
                    product: this.product,
                });
            }
        },

        price: {
            get () {
                return this.form.price;
            },
            set (newValue) {
                this.$emit('productUpdate', {
                    cost: this.form.cost,
                    price: newValue ? Number(newValue) : 0,
                    price_2plus: this.price_2plus ? Number(this.price_2plus) : null,
                    market_cost: this.form.market_cost,
                    product: this.product,
                });
            }
        },

        market_cost: {
            get () {
                return this.form.market_cost;
            },
            set (newValue) {
                this.$emit('productUpdate', {
                    market_cost: newValue !== null && newValue !== '' ? Number(newValue) : null,
                    cost: this.form.cost,
                    price: this.price,
                    price_2plus: this.price_2plus,
                    product: this.product,
                });

                this.setUpdatedAt();
            }
        },

        hasUnsavedChanges () {
            return this.filters.unsaved_only === false || this.form.hasUpdated;
        },

        isMarketCostCategory() {
            if (!this.product || !this.product.category_id) return false;

            const cat = this.categories.find(c => c.id === this.product.category_id);
            if (!cat) return false;

            // Market cost categories: 99 (Produce) and its subcategories
            const marketCostCategories = [99];
            return marketCostCategories.includes(cat.id) || marketCostCategories.includes(cat.parent_id);
        },

        key () {
            return getCurrentInstance().vnode.key;
        },

        // Get the index of this product in the submitted array (updatedProducts)
        submittedIndex () {
            if (!this.updatedProducts || this.updatedProducts.length === 0) {
                return null;
            }
            const index = this.updatedProducts.findIndex(p => p.id === this.form.id);
            return index !== -1 ? index : null;
        },

        productUrl () {
            return route('products.edit', {
                product: this.product.id,
                return: route('pricing.index', this.filters),
            });
        },
    },


    methods: {
        modifyPriceTwoPlus() {
            if (this.type === 'fixed') {
                return 'Fixed Price';
            } else if (this.type === 'disabled') {
                this.form.price_2plus = null;
                return "Disabled";
            }
        },

        toggleEdit () {
            this.$emit('edit-toggle', {
                editing: !this.editing,
                id: this.form.id,
            });

            // Clearing Highlighted Text from double clicking
            window.getSelection().removeAllRanges();
        },

        handlePriceLevelUpdate (payload) {
            this.$emit('priceLevelUpdate', {
                pricingLevel: payload,
                product: this.product,
            });

            this.setUpdatedAt();
            this.addDirtyPricingLevel(payload.pricing_level_id);
        },

        actualPrice (pricing) {
            if (pricing.modifier_type === 'disabled') {
                return 'X';
            } else if (pricing.price) {
                return pricing.price.toFixed(2)
            }

            return '';
        },

        priceToPlusView (pricing) {
            if (pricing !== null) {
                return pricing.toFixed(2);
            }
        },

        setUpdatedAt (fieldName) {
            this.form.hasUpdated = true;
            if (fieldName && !this.form.dirtyFields.includes(fieldName)) {
                this.form.dirtyFields.push(fieldName);
            }
        },

        addDirtyPricingLevel (pricingLevelId) {
            if (!this.form.dirtyPricingLevelIds.includes(pricingLevelId)) {
                this.form.dirtyPricingLevelIds.push(pricingLevelId);
            }
        },

        toggleSelected () {
            this.$emit('select-toggle', {
                selected: !this.isSelected,
                id: this.form.id,
            });
        },

        isEditingPricingLevel(id) {
            return this.editingPriceLevels.findIndex(l => l === id) !== -1;
        },

        // Get error message for a product field
        getError(field) {
            if (this.submittedIndex === null || !this.errors) {
                return null;
            }
            return this.errors[`products.${this.submittedIndex}.${field}`] || null;
        },

        // Get error message for a pricing level field
        getPricingLevelError(pricingLevelIndex, field) {
            if (this.submittedIndex === null || !this.errors) {
                return null;
            }
            return this.errors[`products.${this.submittedIndex}.pricing_levels.${pricingLevelIndex}.${field}`] || null;
        },
    },

    watch: {
        'form.enabled'() { this.setUpdatedAt('enabled'); },
        'form.local'() { this.setUpdatedAt('local'); },
        'form.hot_buy'() { this.setUpdatedAt('hot_buy'); },
        'form.special_order'(value) {
            if (value) {
                const hours = Number(this.form.special_order_lead_hours);
                if (hours !== 24 && hours !== 48 && hours !== 72) {
                    this.form.special_order_lead_hours = 48;
                }
            } else {
                this.form.special_order_lead_hours = null;
            }

            this.setUpdatedAt('special_order');
        },
        'form.special_order_lead_hours'() { this.setUpdatedAt('special_order_lead_hours'); },
        'form.allow_oversell'() { this.setUpdatedAt('allow_oversell'); },
        'form.price'() { this.setUpdatedAt('price'); },
        'form.price_2plus'() { this.setUpdatedAt('price_2plus'); },
        'form.market_cost'() { this.setUpdatedAt('market_cost'); },

        selected (newValue) {
            this.isSelected = newValue;
        }
    },

    mounted () {
         this.type = this.price_2plus > 0 ? 'fixed' : 'disabled';
    }
}
</script>

<style scoped>
td {
    vertical-align: top;
}

input[type="number"] {
    @apply py-0 px-1;
    max-width: 75px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.price-select {
    width: 150px;
}

.price-input {
    width: 100px;
}

.unsaved-changes {
    @apply bg-yellow-100 !important;
}

select {
    @apply pl-2 py-1 pr-8 border border-gray ring-green;
}

.price-select-sm input[type="number"],
.price-select-sm select {
    @apply py-0;
}
</style>
